'use client'

import type { Blog } from '@/payload-types'

import { RichText } from '@/components/RichText'
import { Calendar, Clock, User, ArrowLeft, Tag } from 'lucide-react'
import Link from 'next/link'
import { getClientSideURL } from '@/utilities/getURL'

type Props = {
  blog: Blog
}

const categoryLabels: Record<string, string> = {
  voeding: 'Voeding',
  fitness: 'Fitness',
  leefstijl: 'Leefstijl',
  recepten: 'Recepten',
  tips: 'Tips',
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return null
  const date = new Date(dateString)
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

type LayoutBlock = NonNullable<Blog['layout']>[number]

const renderBlock = (block: LayoutBlock, index: number) => {
  if (!block) return null

  const key = block.id || `block-${index}`

  switch (block.blockType) {
    case 'courseRichText':
      return (
        <div key={key} className="prose prose-lg max-w-none">
          <RichText
            data={block.content}
            enableGutter={false}
            enableProse={false}
          />
        </div>
      )

    default:
      return null
  }
}

export const BlogPageContent = ({ blog }: Props) => {
  const { title, publishedAt, readingTime, author, category, image, hideImageOnDetail, layout, intro } = blog

  type ImageSize = {
    url?: string | null
    filename?: string | null
    width?: number | null
    height?: number | null
  }

  type UploadedLike = {
    url?: string | null
    filename?: string | null
    alt?: string | null
    focalX?: number | null
    focalY?: number | null
    sizes?: {
      thumbnail?: ImageSize | null
      card?: ImageSize | null
      large?: ImageSize | null
    } | null
  }

  const media = typeof image === 'object' ? (image as UploadedLike) : null
  const serverURL = getClientSideURL()
  const largeSize = media?.sizes?.large
  const imageURL = largeSize?.url
    ? `${serverURL}${largeSize.url}`
    : largeSize?.filename
      ? `${serverURL}/media/${largeSize.filename}`
      : media?.url
        ? `${serverURL}${media.url}`
        : media?.filename
          ? `${serverURL}/media/${media.filename}`
          : null
  const focalX = typeof media?.focalX === 'number' ? media?.focalX : 50
  const focalY = typeof media?.focalY === 'number' ? media?.focalY : 50

  const formattedDate = formatDate(publishedAt)

  return (
    <article className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5 mb-8 pt-8 pb-6">
        <div className="container">
          {/* Back link */}
          <Link
            href="/blog"
            className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Terug naar blog
          </Link>

          <div className="mx-auto max-w-3xl">
            {/* Category badge */}
            {category && (
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                <Tag className="h-3.5 w-3.5" />
                {categoryLabels[category] || category}
              </div>
            )}

            {/* Title */}
            <h1 className="mb-6 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
              {title}
            </h1>

            {/* Intro - only shown on blog card, not on detail page */}

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {author && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{author}</span>
                </div>
              )}
              {formattedDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formattedDate}</span>
                </div>
              )}
              {readingTime && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{readingTime} min leestijd</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {imageURL && !hideImageOnDetail && (
        <section className="container mb-12">
          <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt={media?.alt || title}
              className="aspect-[16/9] w-full object-cover"
              src={imageURL}
              style={{ objectPosition: `${focalX}% ${focalY}%` }}
            />
          </div>
        </section>
      )}

      {/* Content Layout */}
      <section className="pb-16 md:pb-24">
        <div className="container">
          <div className="mx-auto max-w-3xl space-y-8">
            {layout?.map((block, index) => renderBlock(block, index))}
          </div>
        </div>
      </section>

      {/* Back to blog CTA */}
      <section className="border-t border-border/50 bg-muted/30 py-12">
        <div className="container text-center">
          <p className="mb-4 text-muted-foreground">Meer lezen?</p>
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <ArrowLeft className="h-4 w-4" />
            Bekijk alle artikelen
          </Link>
        </div>
      </section>
    </article>
  )
}
